package terraultima.game

/**
 * Presentation-agnostic view models.
 *
 * Everything a user interface needs to draw the game, and NOTHING about how to draw it: no ANSI, no
 * widths, no key handling, no terminal. A second interface (web, GUI, Slack bot) implements
 * [Frontend] against these types and touches no engine code.
 *
 * Rule for this file: if a field would only make sense in a terminal, it does not belong here.
 */

enum class Severity { CALM, NOTABLE, CRISIS, OPPORTUNITY }

data class MotiveView(
    val id: String,
    val line: String,          // "an asteroid nineteen kilometres across will hit the Earth in three years"
    val name: String,
    val teaches: String,
    val detail: String
)

/**
 * A gauge as the player may see it: a band and a word, never a number (D003 §3.2).
 *
 * [means] is what the label is a label OF — "whether your authority is accepted" for standing.
 * Without it the readout says "tolerated standing", which is two abstractions and no fact; a
 * reader quoted it back as unintelligible and was right to.
 */
data class GaugeView(val key: String, val label: String, val band: Int, val bandCount: Int,
                     val word: String, val means: String = "")

data class StockView(
    val label: String, val amount: Double, val note: String = "", val low: Boolean = false,
    /** -1 falling, 0 steady, +1 rising, over the last few years. */
    val trend: Int = 0,
    /** e.g. "falling 3 yrs" — the thing that turns a number into a reason to act. */
    val trendNote: String = ""
)

data class ColonyView(
    val shipName: String,
    val year: Int,
    /** 1..12 within the mission year. The clock runs in months; only showing years hid that. */
    val monthOfYear: Int,
    val place: String,
    /**
     * The colony has reached its target and has not landed: the window where the planet IS the
     * decision. The interface shows what is known about it directly here rather than behind a key,
     * because "commit to landing" cannot be answered from a status line about food and power.
     */
    val atPlanet: Boolean,
    val souls: Int,
    val adults: Int,
    val children: Int,
    val elders: Int,
    val asleep: Int,
    val stocks: List<StockView>,
    val powerMade: Double,
    val powerDrawn: Double,
    val powerNote: String,
    val powerShort: Boolean,
    /**
     * Net kW the colony's own decisions have committed (negative) or freed (positive), already
     * inside `powerDrawn`. Shown because an option that says "a season of instrument time" has to
     * land on a number the player can watch — and watch lapse when the season is over (D008).
     */
    val powerCommitted: Double,
    val gauges: List<GaugeView>,
    val cultivated: Double,
    /**
     * People who fit in the storm shelter, and 0 when there is none. Shown because an event says
     * "there is room for perhaps two thirds of you": a claim about the colony the player cannot
     * check reads as arbitrary (A3), and the fix is a visible parameter, not softer wording.
     */
    val shelterFor: Int,
    /**
     * Survey data. Ten options in the corpus pay in it, and until now the player never saw it and
     * was never told what it is for: it accumulates into the colony's SKILL baseline
     * (`competence` chases +0.20 x data/100). A currency you cannot see and whose purpose is
     * unstated is a payoff that reads as nothing.
     */
    val surveyData: Double,
    val shortHanded: Boolean,
    val labourPct: Int,
    val capabilities: List<String>,   // short human labels
    val broken: List<String>,
    /** DESIGNER VIEW ONLY: the hidden pressures, 0..1. */
    val pressures: List<Pair<String, Double>> = emptyList(),
    /**
     * PRESSURES THE COLONY HAS ALREADY SHOWN THE PLAYER, as a word on a rung — never as the 0..1
     * value. A pressure appears here only once a symptom event for it has fired, so visibility is
     * earned through fiction rather than granted by a panel.
     *
     * Why this exists: round 1 put pressure DIRECTIONS on the price line ("gains: less
     * resentment"), which stopped 39 options reading as pure fines. Iteration 2's readers then hit
     * the next layer — *"I'm asked to spend and gain quantities I cannot see, cannot check the
     * level of... is drift at 10% or 90%?"* — with one run counting eight such currencies and every
     * single reviewer flagging at least one. This is the level, for the ones the game has already
     * named out loud.
     */
    val strains: List<GaugeView> = emptyList(),
    /** DESIGNER VIEW ONLY: the identity axes, -1..1, derived from decision tags. */
    val axes: List<Pair<String, Double>> = emptyList(),
    val tension: Double = 0.0
)

/**
 * One thing you may or may not know about the planet. `value == null` means genuinely unknown, and
 * [learnedBy] then says what would have told you — so a player can decide, next run, whether that
 * knowledge is worth buying.
 */
data class PlanetFactView(
    val label: String,
    /** One word, for a compact readout: "quakes", "metal", "plants". */
    val shortLabel: String,
    val confidence: String,      // "unknown" | "estimated" | "measured"
    val text: String,            // rendered value with margin, or "unknown"
    /** The value and its unit with no margin — "0.38 g", "33 %", "?" — for a one-line readout. */
    val brief: String,
    val learnedBy: String,
    /**
     * What the number means for people who have to live there — "crops want lamps, and panels give
     * back less than they promise". Empty while the parameter is unknown.
     *
     * The eleven parameters were a single dense line of physics, which is unreadable at the one
     * moment it decides a century: `sunlight 0.43 x Earth` is not a fact about anybody's life.
     */
    val meaning: String = ""
)

/**
 * Something the colony is working toward, at one of two timescales.
 *
 * The designer's correction: a player must always hold a short-term and a long-term aim, and these
 * are "often but not necessarily the same as problems". Without them every event is a bill and the
 * only sensible equipment is whatever cancels a bill — which is exactly what two cold reads found.
 * Goals turn a crisis into an obstacle to something, which is what makes it cost anything emotionally.
 */
data class GoalView(
    val label: String,
    val progress: Double,        // 0..1
    val detail: String,          // "2,400 of 3,100 m² planted"
    val longTerm: Boolean,
    val done: Boolean = false,
    val blocked: String = ""     // why it is not advancing, if it is not
)

data class ModuleView(
    val name: String,
    val condition: String,            // "ok" | "worn" | "broken"
    val complexity: Double,
    val grants: List<String>,
    val effects: List<String>
)

data class BudgetView(
    val label: String,
    val have: Double,
    val need: Double,
    val unit: String,
    val ok: Boolean,
    /** What happens if this one runs out. Not every budget is lethal, and saying so is the point. */
    val consequence: String
)

data class SetupView(
    val motiveName: String,
    /** "an asteroid nineteen kilometres across will hit the Earth in three years" — the
     *  antecedent [motiveDetail] needs. */
    val motiveLine: String,
    val motiveDetail: String,
    val missionLine: String,
    val planetNote: String,
    val shipName: String,
    val shipNote: String,
    val crossingYears: Int,
    val testingPct: Int,
    val expectedFaultsLow: Double,
    val expectedFaultsHigh: Double,
    val modules: List<ModuleView>,
    val budgets: List<BudgetView>,
    val crewNote: List<String>
)

/**
 * How an option that is NOT available presents itself. Authored per option (`Option.whenLocked`),
 * because the engine cannot tell "the ordinary way down is shut, and here is why" from "you did not
 * bring a rover" from "power is not the problem right now".
 *
 * FULL keeps its number and its whole block, locked, with a reason. NAME keeps neither: it is
 * listed under the options as a bare name, so the player learns the option exists and that this
 * expedition was never going to have it. (HIDE never reaches a frontend at all.)
 */
enum class OptionDisplay { FULL, NAME }

data class OptionView(
    /**
     * 1-based. Numbers what the player can actually see and count: available options and locked
     * ones that render in full. A NAME-only option is not numbered, because a number that cannot
     * be tapped and a gap in the sequence are each read as a bug.
     */
    val number: Int,
    val label: String,
    val hint: String,
    val available: Boolean,
    val needs: List<String>,          // capability names, for a compact "[needs X]"
    val whyNot: String,               // plain-language sentence
    /**
     * DESIGNER VIEW ONLY. What this option actually does, in readable form, including the identity
     * tags it writes. Never shown in normal play — per `00-VISION.md` §4 the hidden layer is hidden
     * from the designer too while playtesting for feel. It exists so that when something reads
     * wrong, you can see whether the writing or the mechanics are at fault.
     */
    val debug: List<String> = emptyList(),
    val tags: List<String> = emptyList(),
    /**
     * "Twelve years of looking at one thing — 12 years" when this option starts an AMBITION.
     *
     * An ambition is the one payoff the player cannot see in the option text: it lands a decade
     * later, and the hint is the only place it could have been mentioned. One of them was not
     * mentioned — "Fund it" on the long experiment reads as pure cost and ends "you will very
     * likely not be in office when it answers", while actually being the largest single gain in
     * the corpus. Naming the undertaking fixes the whole class rather than that one hint.
     */
    val starts: String = "",
    /**
     * "air pressure, accessible water, seismic activity" — what this option would actually settle,
     * for THIS expedition, when it changes what the colony knows.
     *
     * Written hints cannot carry this: what a survey teaches depends entirely on the instruments
     * aboard, so "survey it properly" meant nine different things across twelve builds and nothing
     * at all on a build with no probe. Computed from the knowledge table instead.
     */
    val learns: String = "",
    /**
     * "45 spares · 12 kW of power · a good deal less morale" — computed from the
     * option's own effects, in the units the status panel uses, and therefore incapable of lying.
     *
     * 126 cold readers across two full playtests found the same thing: options are priced in prose
     * ("cheaper", "months of work") while the status block is scrupulously numeric, so a reader
     * picks whichever option happens to state a quantity. An option with no stated cost wins by
     * default. Never carries pressures or axes — the hidden layer stays hidden.
     */
    val pays: List<String> = emptyList(),
    val gains: List<String> = emptyList(),
    /** Its outcome is a gamble, so the price above is what it costs to TRY. */
    val uncertain: Boolean = false,
    /** When [uncertain]: each branch as "3 in 10: ✖ spares 30 · ✖ morale ●", likeliest first. */
    val odds: List<String> = emptyList(),
    /**
     * "nothing you can measure" — said out loud when an option moves no visible quantity at all.
     *
     * Both playtests found that an option with an EMPTY price reads as free and becomes dominant
     * by default, which is the opposite of what a blank line should mean. Saying it explicitly
     * turns the blank into information: this is the one where you do not act, and its costs are
     * the kind the status panel cannot show you.
     */
    val priceNote: String = "",
    /** The player took this exact option the last time this event came up. */
    val takenBefore: Boolean = false,
    /** FULL, or NAME for one that is only worth listing by name. Always FULL when available. */
    val display: OptionDisplay = OptionDisplay.FULL,
    /**
     * The same price as [pays]/[gains], as structure a screen can draw: one chip per effect, a sign
     * and a size. Derived from the effects like the prose, so a new event gets chips for free.
     */
    val chips: List<Chip> = emptyList(),
    /** It costs lives, so a frontend asks twice. */
    val lethal: Boolean = false
)

/**
 * One visible consequence of an option. [text] is already in the panel's units ("morale",
 * "food 2 wk", "1 life"); [dots] is 1..3 for a named quantity without a number, 0 when the text
 * carries its own number.
 */
data class Chip(val text: String, val good: Boolean, val dots: Int = 0, val kind: String = "")

data class DecisionView(
    val title: String,
    val body: String,
    val severity: Severity,
    val year: Int,
    /** 1..12 within the mission year. */
    val monthOfYear: Int,
    /**
     * How long since the last thing the player was asked, in words: "later the same month", "three
     * months later", "eleven years later". Two decisions in one burst are not simultaneous, and a
     * decade of silence is not the same as a fortnight — the interface said neither.
     */
    val sinceLast: String,
    val theme: String,
    /** Everything the player can read: the numbered options first, the name-only ones after. */
    val options: List<OptionView>,
    /** DESIGNER VIEW ONLY: event id, and what the event IS (source, weight, cooldown). */
    val debugId: String = "",
    val debugWhy: String = "",
    /**
     * DESIGNER VIEW ONLY: why this event arrived THIS MONTH — the channel it came down, the deck it
     * was drawn from, the gates it had to pass. One line each, already in reading order.
     */
    val debugSelection: List<String> = emptyList(),
    /**
     * "the third time · you sent them out in year 31" — this event has come round before.
     *
     * Both playtests put repetition at the top of the list, and neither reviewer minded the
     * repetition itself: they minded the AMNESIA. *"Either the game has forgotten, or the year-18
     * choice never changed the world state. The second is worse, because it retroactively makes
     * the best-written outcome in the log cosmetic."* The engine has always known — `lastFired`
     * and the trace are right there — and simply never said.
     */
    val timesBefore: Int = 0,
    val previously: String = "",
    /**
     * What the situation itself is doing whatever the player chooses. A price that lists only the
     * option's own share reads as the whole of the cost, and 57 deaths under a "1 life" option was
     * the result.
     */
    val stakes: String = ""
) {
    /** The numbered options — what the player chooses between. */
    val shown: List<OptionView> get() = options.filter { it.display == OptionDisplay.FULL }
    /** Locked options worth naming and nothing more: "with another ship you could also…". */
    val alsoPossible: List<OptionView> get() = options.filter { it.display == OptionDisplay.NAME }
}

data class YearReportView(
    val year: Int,
    val decisionsThisYear: Int,
    val notes: List<String>,          // symptom lines: the mood of the colony
    val faultsAbsorbed: Int
)

data class HistoryEntry(val year: Int, val title: String, val chosen: String, val severity: Severity)

data class EndingView(
    val tier: String,                 // THRIVING | ESTABLISHED | CLINGING | LOST
    val note: String,
    val alive: Boolean,
    val endReason: String,
    val year: Int,
    val people: Int,
    val decisions: Int,
    /** What the colony became: (strength word, identity word). Nobody chose these. */
    val identity: List<Pair<String, String>>,
    /**
     * What actually happened, in three or four lines: the peak, the worst year and what it was,
     * what got built. A reviewer whose colony starved forty-two people in year 53 was told only
     * "ESTABLISHED" and "decisively humane" — *"the ending never mentions the famine."* An epilogue
     * that cannot see the run is not an epilogue.
     */
    val story: List<String> = emptyList(),
    /**
     * The DECISION behind each identity word, quoted from the run.
     *
     * "Nobody chose those words. They are what you kept deciding" promises decisions and showed
     * none, so the words read as flattery — and one colony was called *decisively open* three rows
     * below its own vignette, "conversation stops when you walk through the aft corridor". A word
     * with the choice that earned it under it can be argued with, which is the point.
     */
    val evidence: List<Pair<String, String>> = emptyList()
)

/** Read-only access a frontend may pull at any time — for an overview key, a sidebar, a web panel. */
interface SessionInfo {
    fun colony(): ColonyView
    fun modules(): List<ModuleView>
    fun budgets(): List<BudgetView>
    fun crewNote(): List<String>
    fun history(): List<HistoryEntry>
    fun planetFacts(): List<PlanetFactView>
    fun planetName(): String
    fun goals(): List<GoalView>
    /** What this world is good FOR, as far as anyone can tell yet. */
    fun planetPotential(): List<String>
}

/**
 * "you cannot sample an atmosphere or fly a drone — that takes atmospheric probes, drone flight".
 *
 * Each entry of [OptionView.needs] is already "<verb> — that takes <kit>", so a frontend that
 * joined them with " or " nested a second em-dash clause inside the first and the sentence stopped
 * parsing: *"you cannot sample an atmosphere — that takes atmospheric probes or fly a drone — that
 * takes drone flight"*. The verbs are the thing the player is weighing; the equipment is one note
 * at the end.
 */
fun lockReason(needs: List<String>): String {
    val verbs = needs.map { it.substringBefore(" — that takes ") }
    val kit = needs.mapNotNull { it.substringAfter(" — that takes ", "").ifEmpty { null } }.distinct()
    return "you cannot " + verbs.joinToString(" or ") +
        (if (kit.isEmpty()) "" else " — that takes " + kit.joinToString(", "))
}

/**
 * "1 elder", "4 children" — the readout printed "1 elders" for the whole of any run with one, which
 * a playtest reader flagged directly.
 */
fun plural(n: Int, one: String, many: String = one + "s"): String =
    "" + n + " " + (if (n == 1) one else many)
